import { useState, useEffect, useRef } from 'react';
import { AlertCircle } from 'lucide-react';
import api from '../../lib/api';

export interface PersonMatch {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  position: string | null;
}

interface PersonDedupeCheckProps {
  firstName: string;
  lastName: string;
  email: string;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  /** Called when the rep picks a match from the dropdown OR the email-blur banner. */
  onMatchSelected: (match: PersonMatch) => void;
  disabled?: boolean;
}

/**
 * Dedupe-aware First / Last / Email subform.
 *
 * As the rep types into name fields, queries existing people by name
 * (2+ chars combined, debounced 300ms) and shows matches below. As they
 * tab off the Email field with a non-empty value, queries by email and
 * shows a single-row warning banner when a match is found. The parent
 * decides what to do with the match — link into a team (Add modal) or
 * route to the detail page (People-page form).
 */
export const PersonDedupeCheck = ({
  firstName,
  lastName,
  email,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onMatchSelected,
  disabled = false,
}: PersonDedupeCheckProps) => {
  const [nameMatches, setNameMatches] = useState<PersonMatch[]>([]);
  const [emailMatch, setEmailMatch] = useState<PersonMatch | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced name typeahead lookup — fires whenever the combined
  // firstName + " " + lastName has 2+ trimmed characters.
  useEffect(() => {
    const combined = `${firstName.trim()} ${lastName.trim()}`.trim();
    if (combined.length < 2) {
      setNameMatches([]);
      setIsSearching(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await api.get(
          `/api/people?search=${encodeURIComponent(combined)}&limit=5`,
        );
        setNameMatches(response.data.people || []);
      } catch {
        // Silent — dedupe is advisory; failure doesn't block submit.
        setNameMatches([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [firstName, lastName]);

  // Email-blur lookup
  const handleEmailBlur = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailMatch(null);
      return;
    }
    try {
      const response = await api.get(
        `/api/people?search=${encodeURIComponent(trimmed)}&limit=1`,
      );
      const match = response.data.people?.[0];
      // Only treat as a match if the email actually matches (the search
      // endpoint does substring match across multiple fields).
      if (match && match.email && match.email.toLowerCase() === trimmed.toLowerCase()) {
        setEmailMatch(match);
      } else {
        setEmailMatch(null);
      }
    } catch {
      setEmailMatch(null);
    }
  };

  return (
    <div className="space-y-3">
      {/* First + Last name */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="dedupe-firstName"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            First name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="dedupe-firstName"
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            disabled={disabled}
            className="input"
            placeholder="Jane"
            autoComplete="off"
          />
        </div>
        <div>
          <label
            htmlFor="dedupe-lastName"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Last name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="dedupe-lastName"
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
            disabled={disabled}
            className="input"
            placeholder="Doe"
            autoComplete="off"
          />
        </div>
      </div>

      {/* Name match dropdown */}
      {nameMatches.length > 0 && (
        <div className="border border-blast-200 dark:border-blast-800/50 bg-blast-50 dark:bg-blast-900/20 rounded-lg max-h-48 overflow-y-auto">
          {nameMatches.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => onMatchSelected(m)}
              disabled={disabled}
              className="w-full flex items-center justify-between px-3 py-2 text-left border-b border-blast-100 dark:border-blast-800/40 last:border-b-0 hover:bg-blast-100/60 dark:hover:bg-blast-900/30 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {m.firstName} {m.lastName}
                </div>
                {(m.email || m.position) && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {m.email}
                    {m.email && m.position ? ' · ' : ''}
                    {m.position}
                  </div>
                )}
              </div>
              <span className="ml-3 flex-shrink-0 inline-flex items-center min-h-9 px-3 text-xs font-semibold bg-blast-600 text-white rounded">
                Use
              </span>
            </button>
          ))}
          <p className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 border-t border-blast-100 dark:border-blast-800/40">
            Or keep typing to add as new
          </p>
        </div>
      )}
      {isSearching && nameMatches.length === 0 && (firstName.trim() + lastName.trim()).length >= 2 && (
        <p className="text-xs text-gray-400 dark:text-gray-500">Checking for matches…</p>
      )}

      {/* Email */}
      <div>
        <label
          htmlFor="dedupe-email"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Email
          <span className="ml-1 text-xs font-normal text-gray-400">or phone required</span>
        </label>
        <input
          type="email"
          id="dedupe-email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          onBlur={handleEmailBlur}
          disabled={disabled}
          className="input"
          placeholder="jane@school.edu"
          autoComplete="off"
        />
        {emailMatch && (
          <div className="mt-2 flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg px-3 py-2">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                This email is already used by{' '}
                <span className="font-medium">
                  {emailMatch.firstName} {emailMatch.lastName}
                </span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => onMatchSelected(emailMatch)}
              disabled={disabled}
              className="flex-shrink-0 inline-flex items-center min-h-9 px-3 text-xs font-semibold bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
            >
              Use
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
